import { AdminLayout } from "./AdminLayout";
import { 
  Users, TrendingUp, FolderKanban, Eye, MousePointer, ArrowUpRight, ArrowDownRight,
  Calendar, BarChart3, PieChart, Activity, Globe, FileText, Mail, Phone
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const Dashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/analytics/dashboard`, { withCredentials: true }),
          axios.get(`${API}/analytics/trends`, { params: { days: dateRange }, withCredentials: true }).catch(() => ({ data: [] }))
        ]);
        setDashboardData(dashboardRes.data);
        setAnalyticsData(analyticsRes.data || []);
      } catch (error) {
        console.error("Error fetching dashboard:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange]);

  const seedDatabase = async () => {
    try {
      await axios.post(`${API}/seed`, {}, { withCredentials: true });
      toast.success("Database seeded with sample data");
      window.location.reload();
    } catch (error) {
      toast.error("Failed to seed database");
    }
  };

  // Calculate trends
  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0) return { value: 0, direction: 'neutral' };
    const change = ((current - previous) / previous) * 100;
    return { 
      value: Math.abs(Math.round(change)), 
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral' 
    };
  };

  const statCards = [
    {
      title: "Total Leads",
      value: dashboardData?.crm?.total_leads || 0,
      change: dashboardData?.crm?.new_leads || 0,
      changeLabel: "this week",
      icon: Users,
      color: "text-[#5F8FB8]",
      bgColor: "bg-[#5F8FB8]/10"
    },
    {
      title: "Page Views",
      value: dashboardData?.today?.page_views || 0,
      trend: calculateTrend(dashboardData?.today?.page_views, dashboardData?.yesterday?.page_views),
      changeLabel: "vs previous",
      icon: Eye,
      color: "text-blue-400",
      bgColor: "bg-blue-400/10"
    },
    {
      title: "Active Deliveries",
      value: dashboardData?.projects?.active || 0,
      change: dashboardData?.projects?.pending_tasks || 0,
      changeLabel: "pending tasks",
      icon: FolderKanban,
      color: "text-green-400",
      bgColor: "bg-green-400/10"
    },
    {
      title: "Pipeline Value",
      value: dashboardData?.crm?.pipeline_value ? `£${(dashboardData.crm.pipeline_value / 1000).toFixed(0)}k` : "£0",
      change: dashboardData?.crm?.total_deals || 0,
      changeLabel: "active deals",
      icon: TrendingUp,
      color: "text-purple-400",
      bgColor: "bg-purple-400/10"
    }
  ];

  // Generate chart data visualization
  const renderMiniChart = (data, color) => {
    if (!data || data.length === 0) return null;
    const max = Math.max(...data, 1);
    const width = 100 / data.length;
    
    return (
      <div className="flex items-end h-12 gap-0.5">
        {data.map((value, i) => (
          <div
            key={i}
            className={`${color} opacity-60 hover:opacity-100 transition-opacity`}
            style={{ width: `${width}%`, height: `${(value / max) * 100}%`, minHeight: '2px' }}
          />
        ))}
      </div>
    );
  };

  // Funnel visualization
  const renderFunnel = () => {
    const funnel = dashboardData?.funnel || {
      visits: 100,
      engaged: 45,
      contact_views: 20,
      conversions: 5
    };
    
    const stages = [
      { label: 'Site Visits', value: funnel.visits || 0, color: 'bg-blue-500' },
      { label: 'Engaged (>30s)', value: funnel.engaged || 0, color: 'bg-cyan-500' },
      { label: 'Contact Page', value: funnel.contact_views || 0, color: 'bg-yellow-500' },
      { label: 'Conversions', value: funnel.conversions || 0, color: 'bg-green-500' }
    ];
    
    const maxValue = Math.max(...stages.map(s => s.value), 1);
    
    return (
      <div className="space-y-3">
        {stages.map((stage, i) => {
          const width = (stage.value / maxValue) * 100;
          const conversionRate = i > 0 && stages[i-1].value > 0 
            ? ((stage.value / stages[i-1].value) * 100).toFixed(0) 
            : null;
          
          return (
            <div key={i}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#8A8A8A]">{stage.label}</span>
                <span className="font-medium">
                  {stage.value.toLocaleString()}
                  {conversionRate && <span className="text-[#8A8A8A] text-xs ml-2">({conversionRate}%)</span>}
                </span>
              </div>
              <div className="h-3 bg-[#2E2E2E] overflow-hidden">
                <div className={`h-full ${stage.color} transition-all`} style={{ width: `${width}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Top pages list
  const renderTopPages = () => {
    const pages = dashboardData?.top_pages || [
      { path: '/', views: 450, title: 'Home' },
      { path: '/services', views: 220, title: 'Services' },
      { path: '/contact', views: 180, title: 'Contact' },
      { path: '/work', views: 120, title: 'Work' },
      { path: '/about', views: 95, title: 'About' }
    ];
    
    const maxViews = Math.max(...pages.map(p => p.views), 1);
    
    return (
      <div className="space-y-2">
        {pages.slice(0, 5).map((page, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="w-6 text-xs text-[#8A8A8A]">{i + 1}.</span>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm truncate">{page.title || page.path}</span>
                <span className="text-sm text-[#8A8A8A]">{page.views}</span>
              </div>
              <div className="h-1 bg-[#2E2E2E] overflow-hidden">
                <div className="h-full bg-[#5F8FB8]" style={{ width: `${(page.views / maxViews) * 100}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Traffic sources
  const renderTrafficSources = () => {
    const sources = dashboardData?.traffic_sources || [
      { source: 'Google', sessions: 380, color: 'bg-blue-500' },
      { source: 'Direct', sessions: 250, color: 'bg-green-500' },
      { source: 'Social', sessions: 120, color: 'bg-purple-500' },
      { source: 'Referral', sessions: 80, color: 'bg-yellow-500' },
      { source: 'Other', sessions: 40, color: 'bg-gray-500' }
    ];
    
    const total = sources.reduce((acc, s) => acc + s.sessions, 0);
    
    return (
      <div className="space-y-3">
        <div className="flex h-4 overflow-hidden">
          {sources.map((source, i) => (
            <div 
              key={i}
              className={`${source.color} first:rounded-l last:rounded-r`}
              style={{ width: `${(source.sessions / total) * 100}%` }}
            />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {sources.map((source, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <div className={`w-3 h-3 ${source.color}`} />
              <span className="text-[#8A8A8A]">{source.source}</span>
              <span className="ml-auto">{((source.sessions / total) * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Recent activity
  const renderRecentActivity = () => {
    const activities = dashboardData?.recent_activities || [
      { type: 'lead', message: 'New lead from contact form', time: '5 min ago', icon: Users },
      { type: 'deal', message: 'Deal moved to Proposal stage', time: '1 hour ago', icon: TrendingUp },
      { type: 'project', message: 'Task completed: Homepage Design', time: '2 hours ago', icon: FolderKanban },
      { type: 'email', message: 'Email thread linked to contact', time: '3 hours ago', icon: Mail }
    ];
    
    return (
      <div className="space-y-3">
        {activities.map((activity, i) => {
          const Icon = activity.icon || Activity;
          return (
            <div key={i} className="flex items-start gap-3 py-2 border-b border-[#2E2E2E] last:border-0">
              <div className="w-8 h-8 bg-[#2E2E2E] flex items-center justify-center text-[#8A8A8A]">
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{activity.message}</p>
                <p className="text-xs text-[#8A8A8A]">{activity.time}</p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Deal pipeline mini view
  const renderPipeline = () => {
    const pipeline = dashboardData?.crm?.pipeline || [
      { stage: 'New', count: 5, value: 25000 },
      { stage: 'Qualified', count: 3, value: 45000 },
      { stage: 'Proposal', count: 4, value: 80000 },
      { stage: 'Negotiation', count: 2, value: 35000 }
    ];
    
    const colors = {
      'New': 'bg-blue-500',
      'Qualified': 'bg-cyan-500',
      'Proposal': 'bg-yellow-500',
      'Negotiation': 'bg-purple-500',
      'Won': 'bg-green-500'
    };
    
    return (
      <div className="grid grid-cols-4 gap-2">
        {pipeline.slice(0, 4).map((stage, i) => (
          <div key={i} className="text-center">
            <div className={`h-1 ${colors[stage.stage] || 'bg-gray-500'} mb-2`} />
            <div className="text-lg font-bold">{stage.count}</div>
            <div className="text-xs text-[#8A8A8A] truncate">{stage.stage}</div>
            <div className="text-xs text-[#5F8FB8]">£{(stage.value / 1000).toFixed(0)}k</div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-[#1C1C1C] border border-[#2E2E2E] p-6 animate-pulse">
                <div className="h-4 w-24 bg-[#2E2E2E] rounded mb-4" />
                <div className="h-8 w-16 bg-[#2E2E2E] rounded" />
              </div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Overview">
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Overview</h2>
            <p className="text-sm text-[#8A8A8A]">Current status across pipeline and delivery</p>
          </div>
          <div className="flex items-center gap-3">
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className="input-field text-sm py-2"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
            {user?.role === 'admin' && (
              <button onClick={seedDatabase} className="btn-secondary text-sm">
                Load Sample Data
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="bg-[#1C1C1C] border border-[#2E2E2E] p-6 hover:border-[#5F8FB8]/30 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#8A8A8A] mb-1">{stat.title}</p>
                    <p className="text-3xl font-black">{stat.value}</p>
                    <div className="flex items-center gap-1 mt-2 text-sm">
                      {stat.trend ? (
                        <>
                          {stat.trend.direction === 'up' ? (
                            <ArrowUpRight className="w-4 h-4 text-green-400" />
                          ) : stat.trend.direction === 'down' ? (
                            <ArrowDownRight className="w-4 h-4 text-red-400" />
                          ) : null}
                          <span className={stat.trend.direction === 'up' ? 'text-green-400' : stat.trend.direction === 'down' ? 'text-red-400' : 'text-[#8A8A8A]'}>
                            {stat.trend.value}%
                          </span>
                          <span className="text-[#8A8A8A]">{stat.changeLabel}</span>
                        </>
                      ) : (
                        <>
                          <span className="text-[#5F8FB8]">{stat.change}</span>
                          <span className="text-[#8A8A8A]">{stat.changeLabel}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className={`w-12 h-12 ${stat.bgColor} flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Analytics */}
          <div className="lg:col-span-2 space-y-6">
            {/* Conversion Funnel */}
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-medium text-sm flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#5F8FB8]" />
                  Conversion Funnel
                </h3>
                <span className="text-xs text-[#6B7280]">Last {dateRange} days</span>
              </div>
              {renderFunnel()}
            </div>

            {/* Two Column Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Pages */}
              <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
                <h3 className="font-medium text-sm flex items-center gap-2 mb-4">
                  <FileText className="w-4 h-4 text-[#5F8FB8]" />
                  Top Pages
                </h3>
                {renderTopPages()}
              </div>

              {/* Traffic Sources */}
              <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
                <h3 className="font-medium text-sm flex items-center gap-2 mb-4">
                  <Globe className="w-4 h-4 text-[#5F8FB8]" />
                  Traffic Sources
                </h3>
                {renderTrafficSources()}
              </div>
            </div>

            {/* Deal Pipeline */}
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-medium text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-[#5F8FB8]" />
                  Pipeline Status
                </h3>
                <a href="/admin/deals" className="text-xs text-[#5F8FB8] hover:underline">View all</a>
              </div>
              {renderPipeline()}
            </div>
          </div>

          {/* Right Column - Activity & Quick Stats */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
              <h3 className="font-medium text-sm mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-2">
                <a href="/admin/leads" className="p-3 bg-[#0E0E0E] border border-[#2E2E2E] hover:border-[#5F8FB8]/50 transition-colors text-center">
                  <Users className="w-5 h-5 mx-auto mb-1 text-[#5F8FB8]" />
                  <span className="text-xs">Add Lead</span>
                </a>
                <a href="/admin/deals" className="p-3 bg-[#0E0E0E] border border-[#2E2E2E] hover:border-[#5F8FB8]/50 transition-colors text-center">
                  <TrendingUp className="w-5 h-5 mx-auto mb-1 text-[#5F8FB8]" />
                  <span className="text-xs">Add Deal</span>
                </a>
                <a href="/admin/tasks" className="p-3 bg-[#0E0E0E] border border-[#2E2E2E] hover:border-[#5F8FB8]/50 transition-colors text-center">
                  <FolderKanban className="w-5 h-5 mx-auto mb-1 text-[#5F8FB8]" />
                  <span className="text-xs">Add Task</span>
                </a>
                <a href="/admin/blog" className="p-3 bg-[#0E0E0E] border border-[#2E2E2E] hover:border-[#5F8FB8]/50 transition-colors text-center">
                  <FileText className="w-5 h-5 mx-auto mb-1 text-[#5F8FB8]" />
                  <span className="text-xs">Add Content</span>
                </a>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
              <h3 className="font-medium text-sm mb-4 flex items-center gap-2">
                <Activity className="w-4 h-4 text-[#5F8FB8]" />
                Recent Activity
              </h3>
              {renderRecentActivity()}
            </div>

            {/* Upcoming */}
            <div className="bg-[#1C1C1C] border border-[#2E2E2E] p-6">
              <h3 className="font-medium text-sm mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#5F8FB8]" />
                Upcoming
              </h3>
              <div className="space-y-3 text-sm">
                {(dashboardData?.upcoming_reminders || []).length > 0 ? (
                  dashboardData.upcoming_reminders.slice(0, 4).map((reminder, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-[#2E2E2E] last:border-0">
                      <span className="truncate">{reminder.title}</span>
                      <span className="text-xs text-[#8A8A8A] whitespace-nowrap ml-2">
                        {new Date(reminder.due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-[#8A8A8A]">No upcoming reminders</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
